import {
    ValidateCheckoutErrorResponse,
    ValidateCheckoutRequest,
    ValidateCheckoutResponse,
} from "@ssc-booking/api-contracts"
import { useActor } from "@xstate/react"
import { match } from "ts-pattern"
import {
    dataLoadMachine,
    dataLoadTypes,
    interceptErrors,
    RequestResponseChannel,
} from "../../../../../_common/machines/data-load-machine"
import { ECommerceEvent, ECommerceState } from "../e-commerce-machine"

const types = dataLoadTypes(ValidateCheckoutRequest, ValidateCheckoutResponse, ValidateCheckoutErrorResponse)
export type ValidateCheckoutCommandTypes = typeof types
export type ValidateCheckoutCommandState = ValidateCheckoutCommandTypes["State"]
export type ValidateCheckoutCommandSend = ValidateCheckoutCommandTypes["Send"]
export type ValidateCheckoutCommandActorRef = ValidateCheckoutCommandTypes["ActorRef"]

const notifyParent = (
    ctx: ValidateCheckoutCommandTypes["Context"],
    ev: ValidateCheckoutCommandTypes["Events"],
): ECommerceEvent | undefined =>
    interceptErrors(
        (_, ev) =>
            match(ev)
                .with({ type: "LOAD_SUCCESS" }, (ev) => ({
                    type: "CHECKOUT_VALIDATED" as const,
                    response: ev.response,
                }))
                .with({ type: "LOAD_ERROR" }, (ev) => ({
                    type: "CHECKOUT_NOT_VALIDATED" as const,
                    response: ev.response,
                }))
                .otherwise(() => undefined),
        ctx,
        ev,
    )

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const buildValidateCheckoutCommand = (websocketClient: RequestResponseChannel) =>
    dataLoadMachine({
        id: "validate-checkout",
        channel: websocketClient,
        Request: ValidateCheckoutRequest,
        Success: ValidateCheckoutResponse,
        successType: "ValidateCheckoutResponse",
        Error: ValidateCheckoutErrorResponse,
        errorType: "ValidateCheckoutErrorResponse",
        action: "validate-checkout",
        notifyParent,
    })

export const useValidateCheckoutCommand = (
    state: ECommerceState,
): [ValidateCheckoutCommandState, ValidateCheckoutCommandSend] => {
    const validateCheckoutCommand = state.children.validateCheckoutCommand as ValidateCheckoutCommandActorRef
    return useActor(validateCheckoutCommand) as unknown as [ValidateCheckoutCommandState, ValidateCheckoutCommandSend]
}
